import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  currentProfileId,
  describeError,
  profileAskedInThisTab,
  rememberProfileAsked,
  setCurrentProfileId,
} from './api/client.js';
import { getDeviceProfiles, getAuthState, getClientConfig, logout } from './api/endpoints.js';
import type { AuthState } from '@lumos/contracts';
import { HomePage } from './pages/HomePage.js';
import { TitlePage } from './pages/TitlePage.js';
import { ComingSoonPage } from './pages/ComingSoonPage.js';
import { MoviesPage } from './pages/MoviesPage.js';
import { TvPage } from './pages/TvPage.js';
import { LibraryPage } from './pages/LibraryPage.js';
import { LiveTvPage } from './pages/LiveTvPage.js';
import { PersonPage } from './pages/PersonPage.js';
import './styles.css';
import { createRoot } from 'react-dom/client';
import { LoginPage } from './pages/LoginPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { AppSplash } from './components/AppSplash.js';
import { AppShell } from './components/AppShell.js';
import { ContinueIcon, FavoritesIcon, MoviesIcon, TvIcon } from './components/icons.js';
import { hashAfterProfileChoice, shouldAskProfile } from './pages/profile-gate.js';
import { forgetLocalProgress } from './pages/player-progress.js';
import { isNavKey, parseAdminHash, parseHash } from './components/navigation.js';
import type { NavKey } from './components/navigation.js';
import { installFocusReveal } from './components/focus-reveal.js';
import { installScrollFade } from './components/scroll-fade.js';
import { Loader } from './components/Loader.js';
import { LaunchLayer } from './launch/LaunchLayer.js';
import { launchRouteCommitted } from './launch/launch-store.js';

/*
 * Плеер и админка — отдельными чанками. Плеер тянет hls.js (под четыреста
 * килобайт), админка — монитор и настройки; зритель, открывший главную, не
 * должен ждать ни того, ни другого. Загрузка начинается по первому переходу и
 * дальше берётся из кэша браузера.
 */
const PlayerPage = lazy(() => import('./pages/PlayerPage.js').then((module) => ({ default: module.PlayerPage })));
const AdminPage = lazy(() => import('./pages/AdminPage.js').then((module) => ({ default: module.AdminPage })));

/**
 * Пока чанк едет: у плеера — тёмный экран без надписи (он и так тёмный), у
 * админки — стандартный знак ожидания страницы, по центру.
 */
function ChunkFallback({ dark = false }: { dark?: boolean }): React.JSX.Element {
  return dark ? <main className="chunk-fallback" aria-busy="true" /> : (
    <main className="app" aria-busy="true"><Loader variant="area" label="Загрузка…" /></main>
  );
}

function useHashRoute(): ReturnType<typeof parseHash> {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

const COMING_SOON: Record<
  Exclude<NavKey, 'home'>,
  { title: string; description: string; icon: React.ReactNode }
> = {
  movies: {
    title: 'Фильмы',
    description: 'Отдельный каталог фильмов с фильтрами ещё не подключён на сервере.',
    icon: <MoviesIcon />,
  },
  tv: {
    title: 'Сериалы',
    description: 'Отдельный каталог сериалов ещё не подключён на сервере.',
    icon: <TvIcon />,
  },
  favorites: {
    title: 'Избранное',
    description: 'Сохранение избранного появится, когда на сервере будет готов соответствующий API.',
    icon: <FavoritesIcon />,
  },
  continue: {
    title: 'История просмотра',
    description: 'История просмотра появится, когда на сервере будет готов соответствующий API.',
    icon: <ContinueIcon />,
  },
  livetv: {
    title: 'ТВ',
    description: 'Live TV не настроен на сервере.',
    icon: <TvIcon />,
  },
};

/** Маршрутизируемое содержимое: главная, страница контента или coming-soon. */
function routePage(route: ReturnType<typeof parseHash>): React.JSX.Element {
  if (route.name === 'watch' && route.sessionId) {
    // `key` по сессии — не украшение: при переходе к следующей серии меняется
    // всё, что плеер о потоке знает, и полтора десятка ссылок на прежний поток
    // пережили бы смену пропсов. Новая сессия — новый экран, как на приставке.
    return (
      <Suspense fallback={<ChunkFallback dark />}>
        <PlayerPage
          key={route.sessionId}
          sessionId={route.sessionId}
          contentId={route.contentId}
          restart={route.restart}
        />
      </Suspense>
    );
  }
  if (route.name === 'title' && route.contentId) {
    return <TitlePage contentId={route.contentId} step={route.step} kind={route.kind} />;
  }
  if (route.name === 'person' && route.personId) {
    // `key` по человеку: переход от актёра к актёру (из его же фильма) меняет
    // всё содержимое страницы, и переиспользованное состояние показывало бы
    // прежнюю фильмографию под новым именем.
    return <PersonPage key={route.personId} personId={route.personId} />;
  }
  if (route.name === 'livetv') return <LiveTvPage />;
  if (route.name === 'movies') return <MoviesPage />;
  if (route.name === 'tv') return <TvPage />;
  if (route.name === 'favorites') return <LibraryPage mode="favorites" />;
  if (route.name === 'continue') return <LibraryPage mode="history" />;
  if (route.name !== 'home' && isNavKey(route.name)) {
    const copy = COMING_SOON[route.name as Exclude<NavKey, 'home'>];
    return <ComingSoonPage title={copy.title} description={copy.description} icon={copy.icon} />;
  }
  return <HomePage />;
}

/**
 * Какой это экран — для смены экрана. Шаги одной карточки (описание → источники
 * → серии) экраном не считаются: название и кадр на них остаются на месте, и
 * проигрывать появление всей страницы заново было бы враньём о переходе.
 */
function screenKey(route: ReturnType<typeof parseHash>): string {
  return [route.name, route.contentId ?? '', route.personId ?? '', route.sessionId ?? ''].join('/');
}

export function App(): React.JSX.Element {
  const route = useHashRoute();
  const key = screenKey(route);
  const first = React.useRef(true);
  // Новый экран открывается с начала: hash-переход прокрутку не трогает, и
  // карточка тайтла, открытая из пятого ряда главной, показывалась бы с
  // середины. Главную не трогаем — к ней возвращаются «назад», и браузер сам
  // ставит её туда, где зритель был.
  React.useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (route.name !== 'home') window.scrollTo(0, 0);
    // Решает только смена экрана (`key`), а не каждый рендер.
  }, [key]);
  // Плеер — без обёртки: он во весь экран, и сдвиг на время появления сделал
  // бы его `position: fixed`-слои на миг частью сдвигаемого блока.
  if (route.name === 'watch') return routePage(route);
  // Обёртка с ключом экрана: новый экран — новый элемент, и его появление
  // (растворение со сдвигом, styles.css `.screen`) проигрывается само.
  return (
    <div className="screen" key={key}>
      {routePage(route)}
    </div>
  );
}

function RootApp(): React.JSX.Element {
  const [auth, setAuth] = useState<AuthState | null>(null);
  // «Кто смотрит?» — при каждом открытии приложения, но только там, где выбор
  // вообще есть: к устройству привязано несколько профилей. С одним профилем
  // экран выбора — лишний шаг между «открыл» и «смотрю».
  // `null` — ещё не знаем, сколько профилей у устройства. Это не то же самое,
  // что «не нужен»: показать каталог до ответа значит показать чужое
  // «продолжить» и через миг заменить его вопросом «кто смотрит?».
  const [needsProfile, setNeedsProfile] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brandName, setBrandName] = useState('Lumos');
  const route = useHashRoute();
  // Маршрут отрисован — слою запуска это нужно знать: по этому сигналу он
  // отпускает растворение страницы в плеер и замечает уход не своим путём.
  React.useLayoutEffect(() => {
    launchRouteCommitted(window.location.hash);
  }, [route]);
  useEffect(() => { getAuthState().then(setAuth).catch((e) => setError(describeError(e))); }, []);
  // Устройство помечено администратором «лёгкой версией» — обычный сайт ему не
  // показываем. Сервер и сам уводит его с адреса портала (`web-static.ts`);
  // здесь — на случай вкладки, открытой до пометки.
  useEffect(() => {
    if (auth?.device?.lite === true) window.location.replace('/lite/');
  }, [auth]);
  // Настройки переехали в админку разделом. Старый адрес не ломаем: открытая
  // вкладка или закладка ведёт туда же, где настройки теперь живут.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#/settings' || hash.startsWith('#/settings/')) {
      window.location.replace(`#/admin/settings${hash.slice('#/settings'.length)}`);
    }
  }, [route]);
  useEffect(() => {
    if (auth === null) return;
    if (!auth.authenticated) {
      // Вход не пройден — спрашивать некого: профили привязаны к устройству
      // через человека, и до входа их список пуст.
      setNeedsProfile(false);
      return;
    }
    getDeviceProfiles()
      .then((response) => {
        if (response.profiles.length < 2) {
          setCurrentProfileId(null);
          rememberProfileAsked(false);
          // Ответить «спрашивать не нужно» ОБЯЗАТЕЛЬНО и здесь: пока ответа нет,
          // приложение держит экран загрузки, и молчаливый выход из этой ветки
          // запирал на нём каждую установку с единственным профилем.
          setNeedsProfile(false);
          return;
        }
        // Спрашиваем заново, если вкладку открыли заново: закрыл приложение —
        // в следующий раз к тому же компьютеру мог сесть другой человек.
        // Перезагрузка страницы вкладку не открывает заново и переспрашивания
        // не стоит.
        setNeedsProfile(
          shouldAskProfile({
            profiles: response.profiles,
            chosenId: currentProfileId(),
            askedInThisTab: profileAskedInThisTab(),
          }),
        );
      })
      // Список профилей не пришёл — не повод запирать зрителя на пустом экране:
      // пускаем дальше с тем профилем, который помним.
      .catch(() => setNeedsProfile(false));
  }, [auth]);
  // Server-driven настройки клиента: название установки применяем к оболочке и
  // заголовку вкладки. Ошибку глушим — дефолт «Lumos» уже стоит.
  useEffect(() => {
    getClientConfig()
      .then((config) => {
        setBrandName(config.ui.brandName);
        document.title = config.ui.brandName;
      })
      .catch(() => undefined);
  }, []);
  if (error) return <main className="app"><p className="state error">{error}</p></main>;
  if (!auth) return <AppSplash label="Проверка авторизации" />;
  // Нужна ли стена входа, решает СЕРВЕР (`loginRequired`), а не клиент.
  //
  // В рабочем контуре (`ENV=PROD`) она заперта: одобрение устройства отвечает
  // «этому компьютеру можно», а вход — «это ты», и каталог с историей
  // принадлежат человеку. На машине разработчика стены нет — иначе каждая
  // пересборка начиналась бы с ввода пароля.
  if (!auth.authenticated && auth.loginRequired) return <LoginPage auth={auth} onLogin={setAuth} />;
  // Даже без стены вход должен быть доступен явно: админка живёт за ним всегда.
  const admin = parseAdminHash(window.location.hash);
  if (!auth.user && (window.location.hash === '#/login' || admin !== null)) {
    return <LoginPage auth={auth} onLogin={setAuth} />;
  }
  if (admin !== null && auth.user) {
    return (
      <Suspense fallback={<ChunkFallback />}>
        <AdminPage user={auth.user} section={admin.section} focusUserId={admin.userId} />
      </Suspense>
    );
  }
  // Выбор профиля идёт до каталога: иначе первый же экран покажет чужое
  // «продолжить», а потом переставится под выбранного.
  if (needsProfile === null) return <AppSplash label="Загружаем профили" />;
  if (needsProfile) {
    return (
      <ProfilePage
        onChoose={(profile) => {
          const previous = currentProfileId();
          setCurrentProfileId(profile.id);
          rememberProfileAsked(true);
          setNeedsProfile(false);
          // Позиция последнего просмотра, запомненная вкладкой, принадлежит
          // ПРЕДЫДУЩЕМУ зрителю: она рисуется поверх серверного ответа, и
          // пережив выбор другого человека, показала бы ему чужой просмотр.
          if (previous !== profile.id) forgetLocalProgress();
          const next = hashAfterProfileChoice(previous, profile.id);
          if (next !== null) window.location.hash = next;
          // Перезагрузка, а не перерисовка: каталог, «продолжить», избранное и
          // личные настройки у профиля свои, и половина открытых экранов
          // осталась бы с данными предыдущего.
          window.location.reload();
        }}
      />
    );
  }
  // Слой запуска стоит рядом с экраном, а не внутри него: и оболочка с меню, и
  // плеер приходят и уходят, а слой между ними обязан уцелеть — он один ведёт
  // зрителя от нажатия на странице до первого кадра в плеере.
  //
  // Плеер — полноэкранный, без навигационной оболочки: рельс слева перекрывал бы
  // видео и управление.
  if (route.name === 'watch') return <><App /><LaunchLayer /></>;
  const active: NavKey = isNavKey(route.name) ? route.name : 'home';
  return (
    <>
    <AppShell
      active={active}
      brandName={brandName}
      user={auth.user}
      onLogout={() => {
        void logout()
          .catch(() => undefined)
          .finally(() => {
            // Вышел — значит на этом устройстве заканчивает не только сессия, но
            // и «я уже выбирал»: следующий вход начинается с вопроса, кто смотрит.
            rememberProfileAsked(false);
            setAuth({ ...auth, authenticated: false, user: null, csrfToken: null });
            window.location.hash = '#/';
          });
      }}
    >
      <App />
    </AppShell>
    <LaunchLayer />
    </>
  );
}

export { RootApp };
// Карточка переехала в свой модуль (см. `components/Card.tsx`); реэкспорт
// оставлен, чтобы страницы, импортирующие её отсюда, продолжали работать.
export { Card, type CardActions } from './components/Card.js';


installFocusReveal();
installScrollFade();

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>,
);
